import type { OntologyAgentEvent } from '@atbash-labs/ontology-agent-wire'

import type { EntityResolution } from '../api/branches/types'
import { getOntologyServiceBaseUrl } from '../config'
import { consumeSSE } from './sse-client'

/**
 * Browser client for the merge-conflict resolution agent SSE endpoint. The
 * merge-review page hands it the conflict set the preview already produced
 * (pre-digested) and gets back picks + questions, which it applies to the
 * resolver and renders inline. The agent is advisory — it only fills the form;
 * the user's Merge click is the only thing that applies anything.
 */

/** One conflicting property, with each side's value rendered for the model. */
export interface MergeConflictAgentField {
  name: string
  base?: string
  target?: string
  source?: string
  /** A scalar a content value may replace; structured props are side-pick only. */
  editable: boolean
}

/** One conflict, digested for the agent (the page owns decoding the tagged props). */
export interface MergeConflictAgentConflict {
  /**
   * The id the agent uses to reference this conflict — printable, so the model
   * can reproduce it (a relationship's real id has a non-printable separator).
   */
  id: string
  /** The real conflict id a pick applies to; equals `id` for nodes. */
  logicalId: string
  entityKind: 'node' | 'relationship'
  label?: string
  /** One side removed the entity / labels diverge — whole-side only. */
  structural: boolean
  /**
   * Both branches newly added this node under the same id (no merge base) — it
   * may be split into two nodes instead of merged. Nodes only.
   */
  addAdd?: boolean
  fields: MergeConflictAgentField[]
  /** What the target ("ours") side did — populated for structural conflicts. */
  targetChange?: string
  /** What the source ("theirs") side did — populated for structural conflicts. */
  sourceChange?: string
}

/** A question the agent asked and the user's answer, replayed on continuation. */
export interface MergeConflictAgentAnswer {
  question: string
  answer: string
  id?: string
}

/** Request body for one turn-batch of the merge-conflict agent. */
export interface MergeConflictAgentRequest {
  targetName: string
  sourceName: string
  conflicts: MergeConflictAgentConflict[]
  /** Ids already resolved (manually or by the agent) — the agent skips them. */
  resolvedIds?: string[]
  /** Prior Q&A so a continued session resumes from the user's answers. */
  answers?: MergeConflictAgentAnswer[]
}

/** A resolution the agent picked, ready to apply to the resolver's choices map. */
export interface MergeConflictAgentPick {
  id: string
  entityKind: 'node' | 'relationship'
  resolution: EntityResolution
  rationale: string
}

/** A question the agent paused to ask. */
export interface MergeConflictAgentQuestion {
  id?: string
  question: string
  options?: string[]
}

export interface RunMergeConflictAgentHandlers {
  /** A pick to merge into the resolver's choices map. */
  onPick: (pick: MergeConflictAgentPick) => void
  /** A question to render inline; the user's answer continues the session. */
  onQuestion: (question: MergeConflictAgentQuestion) => void
  /** The session ended with a summary. */
  onFinalized: (summary: string) => void
  /** A streamed error (the run is over). */
  onError: (message: string) => void
}

/**
 * Run one turn-batch of the agent over SSE: stream picks/questions through the
 * handlers until the agent finishes (a summary) or pauses on a question.
 * Resolves when the stream closes; an aborted signal resolves cleanly.
 */
export async function runMergeConflictAgent(
  request: MergeConflictAgentRequest,
  handlers: RunMergeConflictAgentHandlers,
  signal?: AbortSignal
): Promise<void> {
  await consumeSSE<OntologyAgentEvent>({
    url: `${getOntologyServiceBaseUrl()}/merge-conflict-agent`,
    body: request,
    signal,
    onEvent: (event) => {
      switch (event.type) {
        case 'merge_conflict_pick':
          handlers.onPick({
            id: event.id,
            entityKind: event.entityKind,
            resolution: event.resolution as EntityResolution,
            rationale: event.rationale
          })
          break
        case 'merge_conflict_question':
          handlers.onQuestion({ id: event.id, question: event.question, options: event.options })
          break
        case 'merge_conflict_agent_finalized':
          handlers.onFinalized(event.summary)
          break
        case 'error':
          handlers.onError(event.message)
          break
        default:
          break
      }
    }
  })
}
